import * as THREE from "three";
import { buildModel, defineModel, type Model, type Part } from "../../lib";

// A four-wheeled draught wagon: a long wooden flatbed on a chassis, with a draw
// pole (oja) reaching level forward to an iron hitch eye where a draught animal (ox,
// horse, mule, donkey...) hitches. Built to haul raw resources: it ships empty
// and publishes its bed as a 3x4 grid of cargo sockets the consumer fills with
// carriable goods. Front is +z (the pole points toward where the animal stands
// and pulls).

const WOOD = "#8a6f48"; // bright deck plank
const WOOD_DARK = "#6e563a"; // frame / rails
const WOOD_DEEP = "#5e4426"; // posts / pole
const IRON = "#46413a"; // hubs / fittings
const TYRE = "#33302b"; // wheel rim

// One spoked wheel standing in the y-z plane (axle along x), centred at (x,z).
// Radius 0.15, so the wheel sits on the ground with its centre at y = 0.15.
function wheel(x: number, z: number): Part[] {
  const y = 0.15;
  return [
    // tyre / rim
    {
      geometry: new THREE.TorusGeometry(0.15, 0.028, 6, 14),
      color: TYRE,
      position: [x, y, z],
      rotation: [0, Math.PI / 2, 0],
    },
    // hub
    {
      geometry: new THREE.CylinderGeometry(0.04, 0.04, 0.07, 8),
      color: IRON,
      position: [x, y, z],
      rotation: [0, 0, Math.PI / 2],
    },
    // two crossed spokes (in the wheel's y-z plane)
    {
      geometry: new THREE.BoxGeometry(0.022, 0.27, 0.022),
      color: WOOD_DARK,
      position: [x, y, z],
      rotation: [0.785398, 0, 0],
    },
    {
      geometry: new THREE.BoxGeometry(0.022, 0.27, 0.022),
      color: WOOD_DARK,
      position: [x, y, z],
      rotation: [-0.785398, 0, 0],
    },
  ];
}

// Everything but the cargo: wheels, axles, chassis, long flatbed and the draw
// pole reaching forward to the hitch eye.
function base(): Part[] {
  return [
    ...wheel(0.27, -0.32),
    ...wheel(-0.27, -0.32),
    ...wheel(0.27, 0.32),
    ...wheel(-0.27, 0.32),

    // axles (along x), front and rear
    {
      geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.56, 6),
      color: IRON,
      position: [0, 0.15, -0.32],
      rotation: [0, 0, Math.PI / 2],
    },
    {
      geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.56, 6),
      color: IRON,
      position: [0, 0.15, 0.32],
      rotation: [0, 0, Math.PI / 2],
    },

    // chassis side rails (along z) tying the axles together
    {
      geometry: new THREE.BoxGeometry(0.05, 0.05, 0.82),
      color: WOOD_DEEP,
      position: [0.2, 0.2, 0],
    },
    {
      geometry: new THREE.BoxGeometry(0.05, 0.05, 0.82),
      color: WOOD_DEEP,
      position: [-0.2, 0.2, 0],
    },

    // flatbed floor
    {
      geometry: new THREE.BoxGeometry(0.46, 0.05, 0.8),
      color: WOOD,
      position: [0, 0.25, 0],
    },
    // deck plank lines
    {
      geometry: new THREE.BoxGeometry(0.02, 0.055, 0.8),
      color: WOOD_DARK,
      position: [0.13, 0.252, 0],
    },
    {
      geometry: new THREE.BoxGeometry(0.02, 0.055, 0.8),
      color: WOOD_DARK,
      position: [-0.13, 0.252, 0],
    },

    // side walls
    {
      geometry: new THREE.BoxGeometry(0.04, 0.1, 0.8),
      color: WOOD_DARK,
      position: [0.245, 0.325, 0],
    },
    {
      geometry: new THREE.BoxGeometry(0.04, 0.1, 0.8),
      color: WOOD_DARK,
      position: [-0.245, 0.325, 0],
    },
    // front and rear walls
    {
      geometry: new THREE.BoxGeometry(0.46, 0.1, 0.04),
      color: WOOD_DARK,
      position: [0, 0.325, 0.38],
    },
    {
      geometry: new THREE.BoxGeometry(0.46, 0.1, 0.04),
      color: WOOD_DARK,
      position: [0, 0.325, -0.38],
    },
    // corner posts
    {
      geometry: new THREE.BoxGeometry(0.05, 0.13, 0.05),
      color: WOOD_DEEP,
      position: [0.245, 0.34, 0.38],
    },
    {
      geometry: new THREE.BoxGeometry(0.05, 0.13, 0.05),
      color: WOOD_DEEP,
      position: [-0.245, 0.34, 0.38],
    },
    {
      geometry: new THREE.BoxGeometry(0.05, 0.13, 0.05),
      color: WOOD_DEEP,
      position: [0.245, 0.34, -0.38],
    },
    {
      geometry: new THREE.BoxGeometry(0.05, 0.13, 0.05),
      color: WOOD_DEEP,
      position: [-0.245, 0.34, -0.38],
    },

    // draw pole (oja): a short, level member projecting straight forward from
    // the front axle to the hitch eye at its tip.
    {
      geometry: new THREE.BoxGeometry(0.06, 0.06, 0.34),
      color: WOOD_DEEP,
      position: [0, 0.25, 0.5],
    },
    // angled braces from the chassis front corners to the pole foot
    {
      geometry: new THREE.BoxGeometry(0.03, 0.03, 0.26),
      color: WOOD_DEEP,
      position: [0.11, 0.24, 0.44],
      rotation: [0, -0.7, 0],
    },
    {
      geometry: new THREE.BoxGeometry(0.03, 0.03, 0.26),
      color: WOOD_DEEP,
      position: [-0.11, 0.24, 0.44],
      rotation: [0, 0.7, 0],
    },
    // iron hitch eye at the pole tip: the wagon ends here, at the ring where
    // the draught animals attach.
    {
      geometry: new THREE.TorusGeometry(0.05, 0.016, 6, 12),
      color: IRON,
      position: [0, 0.25, 0.69],
      rotation: [0, Math.PI / 2, 0],
    },
  ];
}

export const cargoWagon = defineModel({
  id: "cargo-wagon",
  name: "Cargo Wagon",
  tags: ["vehicle", "wagon", "cart", "draught", "transport", "resources", "low-poly"],
  summary: "A four-wheeled wooden wagon with a draw pole and up to 8 draft animal sockets.",
  description:
    "A long flatbed draught wagon: a wooden cargo bed on a four-wheel chassis " +
    "with a draw pole that rises to an iron hitch eye where the draught " +
    "animals attach. Up to 8 draft animal sockets are dynamically arranged in " +
    "front of the pole: the layout adapts to how many are filled and the " +
    "animals' footprints.",
  author: "pavlin.dev",
  footprint: 0.7,
  tiles: { w: 1, h: 2 },
  parts: () => base(),
  animations: [],
  socketLayout: (fills) => {
    const draft = fills.filter((f) => f.socketId.startsWith("draft-"));
    if (draft.length === 0) return {};

    const n = draft.length;

    // Measure the team's real footprint in its forward-facing pose. Animals are
    // authored facing +x and rotated -90° about y to face the wagon's forward
    // (+z), so after the turn an animal's own x-extent becomes its DEPTH (along z)
    // and its z-extent becomes its WIDTH abreast (along x). Take the max over the
    // (possibly mixed) team so everyone lines up on one regular grid.
    let aw = 0.2, ad = 0.4; // width / depth, with small fallbacks
    for (const f of draft) {
      const m = f.model as Model;
      const v = m.variants.find((x) => x.id === m.defaultVariantId) ?? m.variants[0]!;
      const g = buildModel(v.parts());
      g.computeBoundingBox();
      const b = g.boundingBox!;
      ad = Math.max(ad, b.max.x - b.min.x); // x-extent -> depth after the turn
      aw = Math.max(aw, b.max.z - b.min.z); // z-extent -> width after the turn
      g.dispose();
    }

    const TILE = 1.0;           // the team must stay within one 1×N tile lane
    const GAP = 0.1;            // clearance between neighbours
    const colPitch = aw + GAP;  // centre-to-centre abreast
    const rowPitch = ad + GAP;  // centre-to-centre in depth

    // How many fit abreast within one tile (a rank of c spans (c-1)·colPitch + aw):
    const maxAbreast = Math.max(1, Math.floor((TILE + GAP) / colPitch));
    // Prefer up to 3 ranks deep: use the FEWEST columns that keeps rows ≤ 3 (so
    // the team stays narrow), but never wider than one tile. With big animals
    // that cap can force more than 3 ranks — width always wins.
    const cols = Math.min(maxAbreast, Math.max(1, Math.ceil(n / 3)));
    const rows = Math.ceil(n / cols);
    const rotation: [number, number, number] = [0, -Math.PI / 2, 0];

    // First rank sits just ahead of the hitch; its rear edge near z = baseZ.
    const baseZ = 1.15;
    const zStart = baseZ + ad / 2;

    const result: Record<string, { position: [number, number, number]; rotation: [number, number, number]; scale: number }> = {};
    for (let i = 0; i < n; i++) {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const colsInRow = row === rows - 1 ? n - row * cols : cols;
      const x = (col - (colsInRow - 1) / 2) * colPitch;
      const z = zStart + row * rowPitch;
      result[draft[i].socketId] = { position: [x, 0, z], rotation, scale: 1 };
    }
    return result;
  },
  sockets: (() => {
    type Slot = { id: string; position: [number, number, number]; scale?: number; slotType: string; description: string };
    const slots: Slot[] = [];

    // ── Cargo bed sockets (3x4 grid on the flatbed) ──────────────────────
    const cols = [-0.13, 0, 0.13];
    const rows = [0.25, 0.08, -0.08, -0.25];
    const labels = ["f", "mf", "mr", "r"];
    const colLabels = ["l", "c", "r"];
    for (let r = 0; r < rows.length; r++) {
      for (let c = 0; c < cols.length; c++) {
        slots.push({
          id: `bed-${labels[r]}${colLabels[c]}`,
          position: [cols[c], 0.28, rows[r]],
          scale: 0.7,
          slotType: "carry",
          description: `Cargo slot, row ${labels[r]} col ${colLabels[c]}.`,
        });
      }
    }

    // ── Draft animal sockets (up to 8) ─────────────────────────────────
    // Positions and scales are placeholders — the socketLayout callback
    // computes them dynamically from the number of filled sockets and the
    // animals' footprints.
    for (let i = 0; i < 8; i++) {
      slots.push({
        id: `draft-${i + 1}`,
        position: [0, 0, 1.6],
        slotType: "draft",
        description: `Draft animal slot ${i + 1} of 8.`,
      });
    }

    return slots;
  })(),
});
